import React, { useEffect, useRef, useState } from 'react';
import styles from './Header.module.scss';
import routes from '@/constants/routes.ts';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { Logo } from '@/components/Logo/Logo.tsx';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import { externalLinks } from '@/constants/externalLinks.ts';
import classNames from 'classnames';
import LanguageMenu from '@/components/LanguageMenu/LanguageMenu.tsx';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import ProfileMenu from '@/components/ProfileMenu/ProfileMenu.tsx';
import ExploreGoodwingsMenu from '@/components/ExploreGoodwingsMenu/ExploreGoodwingsMenu.tsx';
import ButtonLink from '@/components/ui/ButtonLink/ButtonLink.tsx';
import CloseIcon from '@/assets/Icons/Close.svg?react';
import InfoIcon from '@/assets/Icons/Spotnana-Help-Question-Mark.svg?react';
import { useWindowSize } from '@react-hook/window-size';
import { autoUpdate, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react';
import ChevronDown from '@/assets/Icons/Spotnana-Chevron-Down.svg?react';
import breakpoints from '@/constants/breakpoints.ts';
import headerMenuPerRole, { HeaderMenu } from '@/constants/headerLinks.tsx';
import usePersistentPreferencesStore from '@/store/usePersistentPreferencesStore.ts';
import OutsideClickHandler from 'react-outside-click-handler';

const MenuWithSubmenu = ({
	menu,
	activeDropdown,
	handlePopoverOpen,
	handlePopoverClose,
	handlePopoverCloseImmediately,
}: {
	menu: HeaderMenu;
	activeDropdown: string | null;
	handlePopoverOpen: (name: string) => void;
	handlePopoverClose: () => void;
	handlePopoverCloseImmediately: () => void;
}) => {
	const { refs, floatingStyles } = useFloating({
		placement: 'bottom-start',
		middleware: [offset({ mainAxis: 8 }), shift({ padding: 20 })],
		whileElementsMounted: autoUpdate,
	});
	const location = useLocation();

	const isActive = activeDropdown === menu.label.toLowerCase();
	const activeRoute = `${location.pathname}${location.search}`;
	const isSubmenuRouteActive =
		menu.submenu?.some(section =>
			section.links.some(link =>
				link.route.includes('?') ? activeRoute === link.route : location.pathname === link.route,
			),
		) || false;

	return (
		<div>
			<button
				type="button"
				aria-expanded={isActive}
				className={classNames(styles.menuLink, styles.menuLinkPopover, {
					[styles.openPopover]: isActive || isSubmenuRouteActive,
				})}
				ref={refs.setReference}
				onClick={() => (isActive ? handlePopoverCloseImmediately() : handlePopoverOpen(menu.label.toLowerCase()))}
				onMouseEnter={() => handlePopoverOpen(menu.label.toLowerCase())}
				onMouseLeave={handlePopoverClose}
			>
				{menu.label}
				<span className={styles.icon}>
					<ChevronDown className={classNames(isActive && styles.rotate)} />
				</span>
				{isActive && <span className={styles.hoverBridge} aria-hidden="true" />}
			</button>
			{isActive && (
				<FloatingPortal>
					<div className={styles.popoverWrapper}>
						<OutsideClickHandler onOutsideClick={handlePopoverCloseImmediately}>
							<div
								ref={refs.setFloating}
								style={{
									...floatingStyles,
								}}
								className={classNames(styles.popover, {
									[styles.visible]: isActive,
								})}
								onMouseEnter={() => handlePopoverOpen(menu.label.toLowerCase())}
								onMouseLeave={handlePopoverClose}
							>
								{menu.submenu?.map((section, sectionIdx) => (
									<div key={sectionIdx} className={styles.linksSection}>
										<span className={styles.subheader}>{section.header}</span>
										{section.links.map((link, linkIdx) => (
											<Link key={linkIdx} className={styles.linkWrapper} to={link.route}>
												<div className={styles.iconWrapper}>{link.icon}</div>
												{link.label}
											</Link>
										))}
									</div>
								))}
							</div>
						</OutsideClickHandler>
					</div>
				</FloatingPortal>
			)}
		</div>
	);
};

const Header: React.FC = () => {
	const user = useWhoamiStore(state => state.user);
	const [width] = useWindowSize();
	const { isLanguageNotificationSeen, setIsLanguageNotificationSeen } = usePersistentPreferencesStore();
	const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
	const [showExploreMenu, setShowExploreMenu] = useState(false);
	const [showLangDropdown, setShowLangDropdown] = useState(false);
	const closePopoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const location = useLocation();
	const isCorporateLogin = !!matchPath('/login/:organizationSlug', location.pathname);

	const handleChangeLanguageInfo = () => {
		setIsLanguageNotificationSeen(true);
		setShowLangDropdown(true);
	};

	const handlePopoverOpen = (popoverName: string) => {
		if (closePopoverTimeoutRef.current) {
			clearTimeout(closePopoverTimeoutRef.current);
			closePopoverTimeoutRef.current = null;
		}
		setActiveDropdown(popoverName);
	};

	const handlePopoverClose = () => {
		if (closePopoverTimeoutRef.current) clearTimeout(closePopoverTimeoutRef.current);
		closePopoverTimeoutRef.current = setTimeout(() => {
			setActiveDropdown(null);
			closePopoverTimeoutRef.current = null;
		}, 250);
	};

	const handlePopoverCloseImmediately = () => {
		if (closePopoverTimeoutRef.current) {
			clearTimeout(closePopoverTimeoutRef.current);
			closePopoverTimeoutRef.current = null;
		}
		setActiveDropdown(null);
	};

	useEffect(() => {
		if (showExploreMenu) {
			document.body.style.overflowY = 'hidden';
		}
		return () => {
			document.body.style.overflowY = 'auto';
		};
	}, [showExploreMenu]);

	useEffect(() => {
		return () => {
			if (closePopoverTimeoutRef.current) clearTimeout(closePopoverTimeoutRef.current);
		};
	}, []);

	const menus: HeaderMenu[] =
		user && user.spotnanaRole && headerMenuPerRole[user.spotnanaRole]
			? headerMenuPerRole[user.spotnanaRole]
			: headerMenuPerRole.default;
	const desktopMenus = menus.filter(menu => menu.label !== 'Support');
	const supportMenu = menus.find(menu => menu.label === 'Support');
	const activeRoute = `${location.pathname}${location.search}`;
	const isMenuActive = (menu: HeaderMenu) => {
		if (menu.match) return location.pathname.includes(menu.match);
		if (!menu.route) return false;
		if (menu.route.includes('?')) return activeRoute === menu.route;

		return location.pathname === menu.route;
	};

	return (
		<div className={styles.headerWrapper}>
			<OutsideClickHandler onOutsideClick={() => setShowExploreMenu(false)} display="flex">
				<div className={styles.headerContent}>
					<FlexBlock alignItems="center">
						<Link to={routes.myClimate} className={styles.logoLink}>
							<Logo className={styles.logotype} />
							<Logo showOnlyLeaves className={styles.logo} />
						</Link>
						{!user && !isCorporateLogin && (
							<Link to={externalLinks.hubspot} className={styles.exploreLink}>
								Explore
							</Link>
						)}
					</FlexBlock>

					{user ? (
						<>
							<div className={styles.menuWrapper}>
								{width > breakpoints.desktop &&
									desktopMenus &&
									desktopMenus.map((menu, idx) =>
										menu.submenu ? (
											<MenuWithSubmenu
												key={idx}
												menu={menu}
												activeDropdown={activeDropdown}
												handlePopoverOpen={handlePopoverOpen}
												handlePopoverClose={handlePopoverClose}
												handlePopoverCloseImmediately={handlePopoverCloseImmediately}
											/>
										) : (
											<Link
												key={idx}
												to={menu.route || ''}
												className={classNames(styles.menuLink, {
													[styles.activeLink]: isMenuActive(menu),
												})}
											>
												{menu.label}
											</Link>
										),
									)}
							</div>
							<FlexBlock flexDirection="row" alignItems="center" className={styles.utilityMenu}>
								{width > breakpoints.desktop && supportMenu?.route && (
									<Link to={supportMenu.route} className={styles.supportLink}>
										<InfoIcon aria-hidden="true" />
										{supportMenu.label}
									</Link>
								)}
								<ProfileMenu />
							</FlexBlock>
						</>
					) : (
						<>
							<FlexBlock
								flexDirection="row"
								columnGap="15px"
								alignItems="center"
								className={styles.notLoggedInMenuWrapper}
							>
								<div className={styles.changeLanguageWrapper}>
									<LanguageMenu showDropdown={showLangDropdown} />
									{isLanguageNotificationSeen && (
										<button onClick={handleChangeLanguageInfo} className={styles.info}>
											<p>Change Language?</p>
											<CloseIcon />
										</button>
									)}
								</div>
								{!isCorporateLogin && (
									<>
										<Link to={routes.login} className={styles.signInLink}>
											Sign in
										</Link>
										<ButtonLink
											href={externalLinks.bookADemo}
											color="green"
											className={styles.bookADemoLink}
										>
											Book a demo
										</ButtonLink>
									</>
								)}
							</FlexBlock>
							{showExploreMenu && <ExploreGoodwingsMenu />}
						</>
					)}
				</div>
			</OutsideClickHandler>
		</div>
	);
};

export default Header;
